import {
  DEFAULT_ASSISTANT_PANEL_WIDTH_RATIO,
  DEFAULT_SOURCES_PANEL_WIDTH_RATIO,
  MAX_ASSISTANT_PANEL_WIDTH_RATIO,
  MAX_SOURCES_PANEL_WIDTH_RATIO,
  MIN_ASSISTANT_RAIL_WIDTH,
  MIN_DOCUMENT_PREVIEW_RATIO,
  MIN_SIDE_PANEL_WIDTH,
  MIN_SOURCES_PANEL_WIDTH,
} from './constants';

export type AssistantStackRequest = {
  chat: number;
  documentOpen: boolean;
  rail: number;
  railOpen: boolean;
  sources: number;
  sourcesOpen: boolean;
  viewport?: number;
};

export type AssistantStackAllocation = {
  chat: number;
  rail: number;
  railOpen: boolean;
  sources: number;
};

export type PackDesiredWidthsRequest = {
  closingDocument: boolean;
  currentChat: number;
  currentSources: number;
  overrideChat?: number;
  overrideSources?: number;
  preferredChat: number;
  preferredSources: number;
};

/**
 * Picks chat/sources to send into {@link allocateAssistantStack}.
 * Closing a document restores the last unconstrained sizes so the preview
 * shrink does not stick after the preview is gone.
 */
export function resolvePackDesiredWidths({
  closingDocument,
  currentChat,
  currentSources,
  overrideChat,
  overrideSources,
  preferredChat,
  preferredSources,
}: PackDesiredWidthsRequest): { chat: number; sources: number } {
  return {
    chat: overrideChat ?? (closingDocument ? preferredChat : currentChat),
    sources:
      overrideSources ?? (closingDocument ? preferredSources : currentSources),
  };
}

export type StoredSourcesWidthRequest = {
  current: number;
  desiredWhenClosed: number;
  packedSources: number;
  sourcesOpen: boolean;
};

/**
 * Width kept in state while sources are closed. Packed output is 0 then, but
 * writing 0 would lose the next-open width. Closing a document still restores
 * {@link desiredWhenClosed} (preferred) so a preview shrink does not stick.
 */
export function resolveStoredSourcesWidth({
  current,
  desiredWhenClosed,
  packedSources,
  sourcesOpen,
}: StoredSourcesWidthRequest): number {
  if (!sourcesOpen && packedSources === 0) {
    return desiredWhenClosed;
  }

  return packedSources === current ? current : packedSources;
}

function resolveViewport(viewport?: number): number {
  return viewport ?? globalThis.window.innerWidth;
}

function ratioPx(viewport: number, ratio: number): number {
  return Math.round(viewport * ratio);
}

function getMaxPanelWidth(maxRatio: number, viewport?: number): number {
  return Math.max(
    MIN_SIDE_PANEL_WIDTH,
    ratioPx(resolveViewport(viewport), maxRatio)
  );
}

function clampPanelWidth(width: number, maxRatio: number): number {
  return Math.min(
    getMaxPanelWidth(maxRatio),
    Math.max(MIN_SIDE_PANEL_WIDTH, width)
  );
}

function getDefaultPanelWidth(defaultRatio: number, maxRatio: number): number {
  return clampPanelWidth(
    ratioPx(globalThis.window.innerWidth, defaultRatio),
    maxRatio
  );
}

function clampSourcesAgainstLeftover(
  width: number,
  leftover: number,
  viewport: number
): number {
  const maxByRatio = Math.max(
    MIN_SOURCES_PANEL_WIDTH,
    ratioPx(viewport, MAX_SOURCES_PANEL_WIDTH_RATIO)
  );
  const maxWidth = Math.max(0, Math.min(maxByRatio, leftover));
  const minWidth = Math.min(MIN_SOURCES_PANEL_WIDTH, maxWidth);

  return Math.min(maxWidth, Math.max(minWidth, width));
}

/** Clamps the chat column width; {@link railWidth} is added separately in CSS when the rail is open. */
export function clampAssistantBasePanelWidth(
  baseWidth: number,
  railWidth: number
): number {
  const maxTotal = getMaxPanelWidth(MAX_ASSISTANT_PANEL_WIDTH_RATIO);
  const maxBase = Math.max(MIN_SIDE_PANEL_WIDTH, maxTotal - railWidth);

  return Math.min(maxBase, Math.max(MIN_SIDE_PANEL_WIDTH, baseWidth));
}

/** Clamps the chat-rail width so the open chat keeps {@link MIN_SIDE_PANEL_WIDTH}. */
export function clampAssistantRailWidth(
  railWidth: number,
  totalWidth: number
): number {
  const maxRail = Math.max(
    MIN_ASSISTANT_RAIL_WIDTH,
    totalWidth - MIN_SIDE_PANEL_WIDTH
  );

  return Math.min(maxRail, Math.max(MIN_ASSISTANT_RAIL_WIDTH, railWidth));
}

/** Grows or shrinks the rail while the open chat width stays fixed. */
export function clampAssistantRailAgainstChat(
  railWidth: number,
  chatWidth: number
): number {
  const maxTotal = getMaxPanelWidth(MAX_ASSISTANT_PANEL_WIDTH_RATIO);
  const reservedChat = Math.max(MIN_SIDE_PANEL_WIDTH, chatWidth);
  const maxRail = Math.max(MIN_ASSISTANT_RAIL_WIDTH, maxTotal - reservedChat);

  return Math.min(maxRail, Math.max(MIN_ASSISTANT_RAIL_WIDTH, railWidth));
}

/**
 * Packs chat, rail, and sources so the stack never overflows the viewport.
 *
 * Chat + open rail stay at most 80%. Sources take leftover space (capped at
 * 60%). Opening a document hides the rail, then shrinks sources, then chat,
 * until the preview keeps at least 20% — even if that means panels drop below
 * their usual minimum widths.
 */
export function allocateAssistantStack(
  request: AssistantStackRequest
): AssistantStackAllocation {
  const viewport = resolveViewport(request.viewport);
  const chatCap = getMaxPanelWidth(MAX_ASSISTANT_PANEL_WIDTH_RATIO, viewport);
  const documentFloor = ratioPx(viewport, MIN_DOCUMENT_PREVIEW_RATIO);

  let railOpen = request.railOpen && !request.documentOpen;
  let rail = railOpen ? Math.max(MIN_ASSISTANT_RAIL_WIDTH, request.rail) : 0;
  let chat = Math.max(MIN_SIDE_PANEL_WIDTH, request.chat);

  let occupied = chat + rail;
  if (occupied > chatCap) {
    if (railOpen) {
      const shrinkRail = Math.min(
        occupied - chatCap,
        rail - MIN_ASSISTANT_RAIL_WIDTH
      );
      if (shrinkRail > 0) {
        rail -= shrinkRail;
        occupied = chat + rail;
      }
    }

    if (occupied > chatCap) {
      chat = Math.max(MIN_SIDE_PANEL_WIDTH, chatCap - rail);
      occupied = chat + rail;
    }

    if (occupied > chatCap && railOpen) {
      railOpen = false;
      rail = 0;
      chat = Math.min(Math.max(MIN_SIDE_PANEL_WIDTH, chat), chatCap);
    }
  }

  let sources = 0;
  if (request.sourcesOpen) {
    const leftover = Math.max(0, viewport - (chat + rail));
    sources = clampSourcesAgainstLeftover(request.sources, leftover, viewport);
  }

  if (request.documentOpen) {
    const maxAssistant = Math.max(0, viewport - documentFloor);
    let overflow = chat + sources - maxAssistant;

    // Soft mins first: shrink sources, then chat, while they stay usable.
    if (overflow > 0 && request.sourcesOpen) {
      const minSources = Math.min(MIN_SOURCES_PANEL_WIDTH, sources);
      const shrinkSources = Math.min(
        overflow,
        Math.max(0, sources - minSources)
      );
      sources -= shrinkSources;
      overflow -= shrinkSources;
    }

    if (overflow > 0) {
      const shrinkChat = Math.min(
        overflow,
        Math.max(0, chat - MIN_SIDE_PANEL_WIDTH)
      );
      chat -= shrinkChat;
      overflow -= shrinkChat;
    }

    // Hard floor: keep the preview at ≥20% even if panels drop below mins.
    if (overflow > 0 && sources > 0) {
      const shrinkSources = Math.min(overflow, sources);
      sources -= shrinkSources;
      overflow -= shrinkSources;
    }

    if (overflow > 0) {
      chat = Math.max(0, chat - overflow);
    }
  }

  return { chat, rail, railOpen, sources };
}

export function getDefaultAssistantPanelWidth(): number {
  return getDefaultPanelWidth(
    DEFAULT_ASSISTANT_PANEL_WIDTH_RATIO,
    MAX_ASSISTANT_PANEL_WIDTH_RATIO
  );
}

export function clampSourcesPanelWidth(
  width: number,
  leftover?: number
): number {
  return clampSourcesAgainstLeftover(
    width,
    leftover ?? globalThis.window.innerWidth,
    globalThis.window.innerWidth
  );
}

export function getDefaultSourcesPanelWidth(): number {
  return getDefaultPanelWidth(
    DEFAULT_SOURCES_PANEL_WIDTH_RATIO,
    MAX_SOURCES_PANEL_WIDTH_RATIO
  );
}
